import { Box, Button, Card, Flex, Heading, Text } from '@chakra-ui/react';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, toaster, useDrawer, useMobileContext, type ConfirmModalHandle } from '@shared';
import { createColumnHelper, type Table } from '@tanstack/react-table';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import announcementsService from '../../api/services/announcementsService';
import AnnouncementDrawer from './AnnouncementDrawer';
import { useDeleteMultipleAnnouncements } from '../../api/hooks/useAnnouncement';
import { useQueryClient } from '@tanstack/react-query';

const columnHelper = createColumnHelper<Announcement>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function Announcements() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<Announcement>(),
		[],
	);
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	const { openDrawer } = useDrawer();

	function getTypeTranslation(type: AnnouncementType): string {
		switch (type) {
			case 'news':
				return t('general.news');
			case 'changeLog':
				return t('general.change_log');
			default:
				return t('general.unknown');
		}
	}

	const columns = [
		columnHelper.accessor('announcementID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('title', {
			header: t('general.title'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('type', {
			header: t('general.type'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return getTypeTranslation(row.original.type);
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
	];

	const { mutate: deleteMultipleAnnouncements } = useDeleteMultipleAnnouncements();
	const queryClient = useQueryClient();
	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);

	const selectedItemsActions = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids: Array<string>, table: Table<Announcement>) => {
				const payload = {
					announcements: ids.map(id => {
						return {
							announcementID: parseInt(id),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_announcements'),
					content: t('general.really_remove_announcements'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});
				if (response === true) {
					deleteMultipleAnnouncements(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['announcements'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	return (
		<Flex flexDirection={'column'} gap={5} width={'100%'} alignItems={'center'} height="100%">
			{!isMobile ? (
				<Card.Root maxWidth="2000px" width="100%">
					<Card.Body>
						<Heading>{t('general.announcements')}</Heading>
					</Card.Body>
				</Card.Root>
			) : null}
			<Flex overflow="auto" width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} maxW={'2000px'}>
				<Flex display={'flex'} flex={1} overflow={'auto'}>
					<Card.Root flex={1} display="flex">
						<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
							<DataCollectionContextProvider
								customQueryKey="announcementsOwnPage"
								dataKey="announcements"
								keyAttribute="announcementID"
								emptyDataText={t('general.no_announcements_found')}
								fetchFn={(...args) => announcementsService.getAllResources(...args)}
								initialItemsPerPage={25}
								columns={columns}
								defaultHiddenColumns={[]}
								isServerSide={true}
								isSelectable={false}
								defaultSort={{ id: 'timeCreated', desc: true }}
								selectedItemsActions={selectedItemsActions}
								onClickRow={row => {
									openDrawer({
										id: 'announcementDrawer',
										isClosedOnInteractionOutside: false,
										content: <AnnouncementDrawer announcementID={row.original.announcementID} />,
									});
								}}
							>
								<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
									<DataCollectionTableHeader />
									<Button
										id="addAnnouncement"
										onClick={() => {
											openDrawer({
												id: 'announcementDrawer',
												isClosedOnInteractionOutside: false,
												content: <AnnouncementDrawer />,
											});
										}}
									>
										{t('general.add_announcement')}
									</Button>
								</Flex>
								<Box flex={1} overflow="auto" minH={0}>
									<DataCollectionTable />
								</Box>
								<Flex gap={2} flexShrink={0}>
									<DataCollectionTableFooter />
									<ConfirmModal ref={confirmRemoveRef} />
								</Flex>
							</DataCollectionContextProvider>
						</Card.Body>
					</Card.Root>
				</Flex>
			</Flex>
		</Flex>
	);
}
